import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useIncidents } from '../../incidents/hooks/useIncidents'
import { useSelectedPropertyId } from '../../../store/propertyStore'
import { teamsService } from '../../../services/teamsService'
import type { Team } from '../../../types/api.types'
import {
  computeCompletedByMonth,
  computeHeatmap,
  computeKpis,
  computeTasksByIntent,
  computeTasksByTeam,
} from '../utils/aggregate'
import type {
  HeatmapCell,
  IncidentTypeData,
  KpiStat,
  MonthlyCompletedData,
  TeamTaskCountData,
} from '../types/analytics.types'

export const ANALYTICS_TEAMS_QUERY_KEY = 'teams' as const

interface AnalyticsData {
  kpis: KpiStat[]
  tasksByTeam: TeamTaskCountData[]
  tasksByIntent: IncidentTypeData[]
  completedByMonth: MonthlyCompletedData[]
  heatmap: HeatmapCell[]
}

interface UseAnalyticsResult {
  data: AnalyticsData | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useAnalytics(): UseAnalyticsResult {
  const propertyId = useSelectedPropertyId()
  const incidentsQuery = useIncidents()
  const teamsQuery = useQuery<Team[]>({
    queryKey: [ANALYTICS_TEAMS_QUERY_KEY, { propertyId }],
    queryFn: () => teamsService.getAll(propertyId ?? undefined),
    meta: { silent: true, source: 'useAnalytics' },
  })

  const incidents = incidentsQuery.data
  const teams = teamsQuery.data

  const data = useMemo<AnalyticsData | undefined>(() => {
    if (!incidents) return undefined
    const teamNames = new Map<number, string>((teams ?? []).map((team) => [team.id, team.name]))
    return {
      kpis: computeKpis(incidents),
      tasksByTeam: computeTasksByTeam(incidents, teamNames),
      tasksByIntent: computeTasksByIntent(incidents),
      completedByMonth: computeCompletedByMonth(incidents, new Date()),
      heatmap: computeHeatmap(incidents),
    }
  }, [incidents, teams])

  return {
    data,
    isLoading: incidentsQuery.isLoading,
    isError: incidentsQuery.isError,
    refetch: () => {
      void incidentsQuery.refetch()
      void teamsQuery.refetch()
    },
  }
}
